import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class SeoTitleStrategy extends TitleStrategy {
  constructor(
    private readonly title: Title,
    private readonly meta: Meta,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    super();
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const resolvedTitle = this.buildTitle(snapshot);
    if (resolvedTitle) {
      this.title.setTitle(resolvedTitle);
      this.meta.updateTag({ property: 'og:title', content: resolvedTitle });
      this.meta.updateTag({ name: 'twitter:title', content: resolvedTitle });
    }

    const deepestRoute = this.getDeepestSnapshot(snapshot.root);
    const description = deepestRoute?.data?.['description'] as string | undefined;
    if (description) {
      this.meta.updateTag({ name: 'description', content: description });
      this.meta.updateTag({ property: 'og:description', content: description });
      this.meta.updateTag({ name: 'twitter:description', content: description });
    }

    const canonical = deepestRoute?.data?.['canonical'] as string | undefined;
    if (canonical) {
      this.setCanonicalUrl(canonical);
    }
  }

  private getDeepestSnapshot(route: ActivatedRouteSnapshot | null): ActivatedRouteSnapshot | null {
    let current = route;
    while (current?.firstChild) {
      current = current.firstChild;
    }
    return current;
  }

  private setCanonicalUrl(url: string): void {
    const head = this.document.head;
    if (!head) {
      return;
    }
    const existing = head.querySelector<HTMLLinkElement>("link[rel='canonical']");
    if (existing) {
      existing.href = url;
      return;
    }
    const linkEl = this.document.createElement('link');
    linkEl.setAttribute('rel', 'canonical');
    linkEl.setAttribute('href', url);
    head.appendChild(linkEl);
  }
}
